import { useState, useCallback } from "react";
import emailjs from "@emailjs/browser";
import { useConfirmToast } from "./alertFunction.tsx";
import { db } from './FirestoreDataLoader.tsx';
import { doc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { v4 } from 'uuid';//(Universally Unique Identifier)


// Funzione per generare il token 
const generateInviteToken = () => { return v4(); };//Cloud Functions-Firebase e SDK Admin ?

const emailTemplates = (
  tipo_invito: string,
  email_invitato: string,
  link_attivazione: string,
  email_mittente: string
) => {
  const email_supporto = 'convertitorefatturepdfxml@gmail.com'

  const attivazioneAzienda = `
    Gentile ${email_invitato}, <br/>
    Siamo lieti di darti il benvenuto in <strong>AutoFattura.pro</strong>, <br/>
    la soluzione progettata per semplificare e velocizzare il processo di conversione e gestione specialmente delle fatture provenienti da fornitori esteri (FPR12).<br/>
    La tua azienda può ora digitalizzare l'interscambio di fatture in modo rapido, trasformando i PDF in formato XML in pochi semplici passaggi.<br/>
    Per iniziare subito, clicca sul link qui sotto e completa l'attivazione del tuo account <strong>Azienda</strong>:<br/><br/>
    ${link_attivazione} <br/><br/>
    Il link di invito può essere utilizzato una sola volta. Qualora non venga utilizzato, scadrà dopo 24 ore.<br/>
    Se hai bisogno di supporto, contattaci via email: ${email_supporto}.<br/>
    L'invito è stato inviato da : ${email_mittente}.<br/><br/>
    Cordiali saluti,<br/>
    Il Team di AutoFattura.pro .
  `;

  const invitoCollaboratore = `
    Gentile ${email_invitato},<br/>
    Hai ricevuto un invito da ${email_mittente} ad unirti alla piattaforma <strong>AutoFattura.pro</strong>.<br/>
    L'attivazione è semplice e ti garantirà l'accesso immediato alle funzionalità necessarie per il tuo lavoro.<br/>
    Clicca sul link sottostante per attivare il tuo account <strong>Collaboratore</strong> : <br/><br/>
    ${link_attivazione} <br/><br/>
    Il link di invito può essere utilizzato una sola volta. Qualora non venga utilizzato, scadrà dopo 24 ore.<br/>
    Se hai bisogno di supporto, contattaci via email: ${email_supporto}.<br/><br/>
    Distinti saluti,<br/>
    Il Team di AutoFattura.pro .
  `;

  const accessoCliente = `
    Gentile Cliente ${email_invitato},<br/>
    ${email_mittente} ha scelto la piattaforma AutoFattura.pro per gestire le fatture provenienti da fornitori esteri (FPR12).<br/>
    Questo servizio ti offre un accesso semplice per inviare le tue fatture in attesa che vengano processate.<br/>
    Per accedere al tuo portale clienti dedicato, ti basterà cliccare sul link sottostante e registrarti :<br/><br/>
    ${link_attivazione}<br/><br/>
    Il link di invito può essere utilizzato una sola volta. Qualora non venga utilizzato, scadrà dopo 24 ore.<br/>
    Se hai bisogno di supporto, contattaci via email: ${email_supporto}.<br/><br/>
    Grazie per la collaborazione,<br/>
    Il Team di AutoFattura.pro .
  `;

  const nuovoFile = `
    Gentile ${email_invitato},<br/>
    ${email_mittente} ha caricato delle nuove fatture !<br/><br/>
    Se hai bisogno di supporto puoi contattarci alla email : ${email_supporto}.<br/>
    Grazie per la collaborazione,<br/>
    Il Team di AutoFattura.pro .
  `;

  const clienteCondiviso = `
    Gentile ${email_invitato},<br/>
    ${email_mittente} ha condiviso la gestione di un cliente con te.<br/><br/>
    Se hai bisogno di supporto puoi contattarci alla email : ${email_supporto}.<br/>
    Grazie per la collaborazione,<br/>
    Il Team di AutoFattura.pro .
  `;

  switch (tipo_invito) {
    case "azienda":
      return attivazioneAzienda;
    case "collaboratore":
      return invitoCollaboratore;
    case "cliente":
      return accessoCliente;
    case "nuovoFile":
      return nuovoFile;
    case "clienteCondiviso":
      return clienteCondiviso;
    default:
      return "";
  }
};

// React hook
type SendMailFunction = (
    tipo_invito: string,
    email_invitato: string,
    email_mittente: string,
    invitanteId?: string
) => Promise<void>;

export const useSendInvite = () => {
    const [sent, setSent] = useState(false);
    const { errorAlert, successAlert } = useConfirmToast();

    // Funzione per inviare la mail
    const sendMail: SendMailFunction = useCallback(async (
        tipo_invito: string,
        email_invitato: string,
        email_mittente: string,
        invitanteId?: string
      ) => {
        setSent(false);

        const inviteToken = generateInviteToken();
        const currentOrigin = window.location.origin;
        const activationLink = `${currentOrigin}/signup?token=${inviteToken}`;
        
        // Calcola la data di scadenza (24 ore)
        const expiryDate = Timestamp.fromMillis(Date.now() + 24 * 60 * 60 * 1000);

        try {
          // SALVATAGGIO DELL'INVITO SU FIRESTORE
          if (invitanteId){ 
            await setDoc(doc(db, 'inviti', inviteToken), {
              email: email_invitato.toLowerCase(),
              ruolo: tipo_invito,
              invitanteId: invitanteId,
              dataCreazione: serverTimestamp(),
              dataScadenza: expiryDate,
              stato: 'inviato',
            });
          }
         
          // Genera il messaggio 
          const message = emailTemplates(
            tipo_invito,
            email_invitato,
            activationLink,
            email_mittente
          );
          
          let subject
          let title
          if (tipo_invito === 'azienda' || tipo_invito === 'collaboratore' || tipo_invito === 'cliente') {
            subject = 'Email di Invito "AutoFattura.pro"';
            title = 'Ottimizza la gestione delle fatture estere (FPR12).';
          } else if (tipo_invito === 'nuovoFile') {
            subject = 'Nuove fatture da elaborare!';
            title = 'Un tuo cliente ha caricato dei nuovi file.';
          } else if (tipo_invito === 'clienteCondiviso'){
            subject = 'Nuove cliente';
            title = 'La tua azienda ha condiviso un cliente.';
          }

          if (!message.trim()) {
            errorAlert("Impossibile inviare: tipo di invito non è valido.");
            return;
          }

          // INVIO EMAIL con EmailJS
          await emailjs.send(
            "service_lxil62m",
            "template_udfsdoq",
            {
              subject : subject,
              title : title,
              email_invitato: email_invitato,
              email_mittente: email_mittente,
              message: message.trim()
            },
            process.env.REACT_APP_API_KEY_EMAILJS
          );
        
          setSent(true);
          if (invitanteId){ 
            successAlert('', 'Email inviata !')
          };

        } catch (err: any) {
            console.error("Errore durante l'invio o il salvataggio su Firestore:", err);
            throw new Error ("Si è verificato un errore durante l'invio.<br/>Controlla la connessione a internet e riprova.");
        }
    }, []);
    
    return { sendMail, sent };

};

